// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import KeyboardEventListener from '@ta/events.keyboard-event-listener';
import { VisibilityObserver } from '@ta/common.is-visible';
import { Backdrop } from '@ta/modal.core';
import { Overlay } from '@ta/overlays.pieces';

type ExternalProps = {|
  /** Transition status as defined by react-transition-group */
  transitionStatus: 'entering' | 'entered' | 'exiting' | 'exited',

  /** The target to flyout from */
  target: ?HTMLElement,

  /** The overlay close function */
  close: () => void,

  /** The scroll-Y offset at which we opened the overlay */
  openedAt: number,

  /** Contents of the overlay */
  overlayContents: React.Node,

  /** Pixel value of the top offset of the overlay from the top of viewport. */
  offsetTop: number,

  /**
   * Length of the fly-in-from-target transition (animation) in milliseconds.
   * 0 will yield no animation.
   */
  transitionTiming: number,
|};

type Props = {|
  ...ExternalProps,

  /**
   * Whether we should close the overlay when the user scrolls away.
   * We don't do this on tablet because the keyboard sliding up seems to cause a page scroll and then
   * force closes immediately after opening. (Mobile is irrelevant since it's a fullscreen overlay.)
   */
  closeOnScrollAway: boolean,
|};

type State = {|
  /** Keep track of whether we've called the overlay closer function yet so we don't call it multiple times from the scroll continuing through the exit animation. */
  closing: boolean,

  /** Necessary for making sure throttled WindowScroll doesn't cause a setState call after dismount. */
  mounted: boolean,
|};

/**
 * The contents of a TargetedFlyout overlay.
 * Separated from TargetedFlyout into its own component because we need to unmount if the overlay hides because checkIfScrolledAway has to
 * be able to track whether it can call setState. Otherwise, the throttled WindowScroll can throw an error if setState is called on dismount.
 */
class TargetedFlyoutContents extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      closing: false,
      mounted: false,
    };

    this.contentsWrapRef = React.createRef();
  }

  componentDidMount() {
    this.setState({ mounted: true });
  }

  componentWillUnmount() {
    this.setState({ mounted: false });
  }

  overlayProps = () => {
    const { target } = this.props;
    const transitionProps = {
      transitionProperties: [
        {
          property: 'top',
          timing: this.props.transitionTiming,
        },
        {
          property: 'left',
          timing: this.props.transitionTiming,
        },
        {
          property: 'transform',
          timing: this.props.transitionTiming,
        },
        {
          property: 'opacity',
          timing: this.props.transitionTiming,
        },
      ],
    };

    if (
      (this.props.transitionStatus === 'exited' || this.props.transitionStatus === 'exiting') &&
      target
    ) {
      const rect = target.getBoundingClientRect();
      /* eslint-disable no-restricted-globals */
      return {
        ...transitionProps,
        opacity: '0',
        position: {
          position: 'absolute',
          top: `${rect.top + window.pageYOffset + target.clientHeight / 2}px`,
          left: `${rect.left + target.clientWidth / 2}px`,
          transform: 'translate(-10%, -10%) scale(.2, .2)',
          transformOrigin: '0 0',
        },
      };
    }

    return {
      ...transitionProps,
      opacity: '1',
      position: {
        position: 'absolute',
        top: `${window.pageYOffset + this.props.offsetTop}px`,
        left: '50%',
        transform: 'translate(-50%, 0) scale(1, 1)',
        transformOrigin: '0 0',
      },
    };
    /* eslint-enable no-restricted-globals */
  };

  checkIfScrolledAway = () => {
    if (!this.state.mounted || !this.props.closeOnScrollAway || this.state.closing) {
      return;
    }

    this.props.close();
    this.setState({ closing: true });
  };

  contentsWrapRef: any;

  render() {
    return (
      <Backdrop onClick={this.props.close}>
        <Overlay {...this.overlayProps()}>
          <VisibilityObserver onInvisible={this.checkIfScrolledAway} node={this.contentsWrapRef}>
            <div ref={this.contentsWrapRef}>{this.props.overlayContents}</div>
          </VisibilityObserver>
        </Overlay>
        <KeyboardEventListener keyName="Escape" callback={this.props.close} />
      </Backdrop>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  // On iPad landscape width can be 1024px which breaks into desktop breakpoint by a single pixel, so let's check userAgentCategory too.
  closeOnScrollAway:
    state.meta.device.viewportCategory === 'DESKTOP' &&
    state.meta.device.userAgentCategory === 'DESKTOP',
}))(TargetedFlyoutContents);
